import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Extracts and formats validation errors from API response
 * @param error - The error object from the API response
 * @returns A formatted error message string that includes both general message and detailed validation errors
 */
export function formatValidationErrors(error: unknown): string {
  try {
    const errorData =
      typeof error === 'object' &&
      error !== null &&
      'response' in error &&
      typeof error.response === 'object' &&
      error.response !== null &&
      'data' in error.response
        ? (error.response.data as Record<string, unknown>)
        : undefined;

    if (!errorData) {
      return 'An unexpected error occurred';
    }

    const message =
      typeof errorData.message === 'string' ? errorData.message : undefined;
    let finalMessage = message || 'An unexpected error occurred';
    const validationErrors: string[] = [];

    // Check for validation errors in the 'errors' field (Laravel validation format)
    if (errorData.errors && typeof errorData.errors === 'object') {
      Object.entries(errorData.errors).forEach(([field, messages]) => {
        if (Array.isArray(messages)) {
          messages.forEach((message: string) => {
            validationErrors.push(`${field}: ${message}`);
          });
        } else if (typeof messages === 'string') {
          validationErrors.push(`${field}: ${messages}`);
        }
      });
    }

    // Also check for validation errors in the 'data' field (fallback)
    if (errorData.data && typeof errorData.data === 'object') {
      Object.entries(errorData.data).forEach(([field, messages]) => {
        if (Array.isArray(messages)) {
          messages.forEach((message: string) => {
            validationErrors.push(`${field}: ${message}`);
          });
        } else if (typeof messages === 'string') {
          validationErrors.push(`${field}: ${messages}`);
        }
      });
    }

    // Combine general message with validation errors
    if (validationErrors.length > 0) {
      finalMessage += '. ' + validationErrors.join(', ');
    }

    return finalMessage;
  } catch {
    return 'An unexpected error occurred';
  }
}

/**
 * Converts snake_case or UPPER_SNAKE_CASE text to camelCase
 * @param text - The text to convert (e.g., "KYC_Form_Link", "user_name", "API_KEY")
 * @returns The camelCase version of the text (e.g., "kycFormLink", "userName", "apiKey")
 */
export function toCamelCase(text: string): string {
  return text
    .toLowerCase()
    .split('_')
    .map((word, index) => {
      if (index === 0) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join('');
}

/**
 * Converts optional text into a trimmed value or undefined when empty.
 */
export function normalizeOptionalString(
  value?: string | null
): string | undefined {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Returns a fallback text when the provided value is null, undefined or blank.
 */
export function formatOptionalText(
  value: string | null | undefined,
  fallback: string
): string {
  if (!value || !value.trim()) {
    return fallback;
  }

  return value;
}
